/**
 * SMART Authentication Provider
 * Handles authentication logic specific to the SMART API system
 */

import { User } from '../../types';
import { IAuthProvider, LoginResponse } from './IAuthProvider';
import { storageService } from '../storageService';
import { configService } from '../configService';
import { trackApiCall, setSentryUser, captureExceptionWithContext, getSentryLogger } from '../../utils/sentryHelpers';

export class SmartAuthProvider implements IAuthProvider {
  /**
   * Authenticate with Smart API
   */
  async login(username: string, password: string, companyCode?: string): Promise<LoginResponse> {
    const loginUrl = configService.getSmartApiUrl(configService.getConfig().smartApi.loginPath);

    return trackApiCall(loginUrl, 'POST', async () => {
      try {
        const response = await fetch(loginUrl, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            userCode: username,
            password: password,
            // (2026-07-24, Jason Cox) Send 'APP' rather than 'PWA' so the Smart
            // backend skips its PWA-only `pda_module.tag_items/manage_tag` 401
            // gate (Login.php). Industry 3 customers (e.g. Nabors, Profrac)
            // don't have the PDA Module menu seeded for their industry, so that
            // gate locked them out entirely. PWA access is now decided
            // client-side in checkPermissions() below. 'APP' still marks the
            // login as mobile in the backend audit log (logFrom = 1).
            loginType: 'APP'
          })
        });

        if (!response.ok) {
          const logger = getSentryLogger();
          logger.error("Smart API HTTP Error", {
            status: response.status,
            statusText: response.statusText,
            url: response.url
          });

          // Handle 401 specifically - user doesn't have permissions
          if (response.status === 401) {
            return {
              success: false,
              error: 'Access denied: You do not have permission to access this application. Please contact your administrator.'
            };
          }

          // For other HTTP errors, try to get error message from response body
          try {
            const errorData = await response.json();
            if (errorData.msg) {
              return {
                success: false,
                error: errorData.msg
              };
            }
          } catch {
            // If we can't parse the response body, continue with generic error
          }

          throw new Error(`Smart API HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.code === 0 && data.data) {
          const userData = data.data;
          const accessToken = userData['access-token'] || userData.access_token;

          // Check for PDA Module permissions
          const hasPDAPermissions = this.checkPermissions(userData);
          if (!hasPDAPermissions) {
            return {
              success: false,
              error: 'Access denied: You do not have the required PDA Module permissions (Manage Tags or Tag Items) to access this application.'
            };
          }

          // Store Smart-specific token
          storageService.setItem('smartAccessToken', accessToken);
          storageService.setItem('authToken', accessToken); // Fallback

          // Store raw permission data for later use (prefer menu over role)
          storageService.setItem('smartPermissions', userData.menu || userData.role || {});

          const user: User = {
            id: userData.id?.toString() || 'smart-user',
            username: username,
            password: '', // Never store password
            email: username,
            firstName: userData.firstName || userData.name || 'Emily',
            lastName: userData.lastName || 'Brown',
            role: 'admin', // Default role, will be overridden by actual permissions
            department: userData.companyName || userData.company || 'Rebound Intl LLC',
            phone: userData.phone || '555-0104',
            lastLogin: new Date().toISOString(),
            isActive: true,
            system: 'smart' as const,
            company: userData.companyName || userData.company || 'Rebound Intl LLC',
            // (2026-04-21, Jason Cox) Also populate the typed `companyName`
            // field so consumers that read the canonical key (e.g. Manual
            // Checkout's "With what company" readout) don't have to know about
            // the legacy `company` alias.
            companyName: userData.companyName || userData.company || undefined,
            companyId: userData.companyId?.toString() || userData.company_id?.toString() || undefined,
            // (2026-08-04) Smart company type (0 platform / 1 customer / 2 vendor);
            // used to gate Manual Checkout away from vendor accounts.
            companyType:
              typeof userData.companyType === 'number'
                ? userData.companyType
                : userData.companyType != null
                  ? Number(userData.companyType)
                  : undefined,
            rawPermissionData: userData // Store raw data for permission checks
          } as User & { rawPermissionData: any };

          // Set Sentry user context
          setSentryUser({
            id: user.id,
            username: user.username,
            email: user.email,
            apiSystem: 'smart'
          });

          return {
            success: true,
            user,
            token: accessToken
          };
        } else {
          return {
            success: false,
            error: data.msg || 'Smart API authentication failed'
          };
        }
      } catch (error: any) {
        const logger = getSentryLogger();
        logger.error("Smart API login error", { error: error.message });
        captureExceptionWithContext(error, {
          tags: { auth_system: 'smart', component: 'SmartAuthProvider' },
          extra: { username }
        });

        // Preserve error message if it's an HTTP error
        if (error.message && error.message.includes('Smart API HTTP')) {
          return {
            success: false,
            error: error.message
          };
        }

        return {
          success: false,
          error: 'Smart API connection failed. Please check your network connection and try again.'
        };
      }
    });
  }

  /**
   * Logout from Smart API
   */
  async logout(token: string): Promise<void> {
    try {
      // Ensure config is loaded before attempting to get API URL
      await configService.loadConfig();

      const response = await fetch(configService.getSmartApiUrl('/base/v1/logout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'access-token': token
        }
      });

      if (!response.ok) {
        throw new Error(`Smart logout HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
    } catch (error: any) {
      console.error('Smart API logout failed:', error);
      throw error;
    }
  }

  /**
   * Validate Smart API token by making a test request
   */
  async validateToken(token: string): Promise<boolean> {
    try {
      // Ensure config is loaded before attempting to get API URL
      await configService.loadConfig();

      const response = await fetch(configService.getSmartApiUrl('/md/v2/listCategory'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'access-token': token
        },
        body: JSON.stringify({
          page: 1,
          pageSize: 1,
          smartboxId: "0",
          workcenterId: "0"
        })
      });

      const data = await response.json();
      return data.code === 0; // Smart API success code
    } catch {
      return false;
    }
  }

  /**
   * Get system type identifier
   */
  getSystemType(): 'smart' {
    return 'smart';
  }

  /**
   * Check if user has required PDA Module permissions
   * Reason: Verifies that user has at least one of the required menu permissions or role permissions
   */
  checkPermissions(userData: any): boolean {
    try {
      let hasTagItems = false;
      let hasManageTag = false;
      let hasUpdateQuantity = false;
      let hasClearTagData = false;

      // Check if user has menu data (new structure)
      if (userData.menu && Array.isArray(userData.menu)) {
        // Flatten the menu structure to find all menu items
        const allMenuItems: any[] = [];

        const flattenMenu = (items: any[]) => {
          items.forEach(item => {
            allMenuItems.push(item);
            if (item.children && Array.isArray(item.children)) {
              flattenMenu(item.children);
            }
          });
        };

        flattenMenu(userData.menu);

        // Look for exact PDA Module permission menuCodes
        hasTagItems = allMenuItems.some(item => item.menuCode === 'tag_items');
        hasManageTag = allMenuItems.some(item => item.menuCode === 'manage_tag');

        // Check md_rfid permissions with specific actions
        const mdRfidItem = allMenuItems.find(item => item.menuCode === 'md_rfid');
        const mdRfidActions = mdRfidItem?.action ? mdRfidItem.action.split(',').map((a: string) => a.trim()) : [];
        hasUpdateQuantity = mdRfidActions.includes('edit_quantity');
        hasClearTagData = mdRfidActions.includes('delete');

        console.log('🔐 Smart API Permission Check (menu structure):', {
          hasTagItems,
          hasManageTag,
          hasUpdateQuantity,
          hasClearTagData,
          mdRfidActions,
          foundPermissions: allMenuItems
            .filter(item =>
              item.menuCode === 'tag_items' ||
              item.menuCode === 'manage_tag' ||
              item.menuCode === 'md_rfid'
            )
            .map(item => ({
              menuCode: item.menuCode,
              menuName: item.menuName,
              action: item.action
            })),
          allMenuCodes: allMenuItems.map(item => item.menuCode)
        });
      }

      // ALSO check role.permissions (user might have both menu and role.permissions!)
      if (userData.role && userData.role.permissions) {
        const permissions = userData.role.permissions;
        // Only update if not already found
        hasTagItems = hasTagItems || permissions.some((p: any) => p.menuCode === 'tag_items');
        hasManageTag = hasManageTag || permissions.some((p: any) => p.menuCode === 'manage_tag');

        // Check md_rfid with actions
        const mdRfidPerm = permissions.find((p: any) => p.menuCode === 'md_rfid');
        const mdRfidActions = mdRfidPerm?.action ? mdRfidPerm.action.split(',').map((a: string) => a.trim()) : [];
        hasUpdateQuantity = hasUpdateQuantity || mdRfidActions.includes('edit_quantity');
        hasClearTagData = hasClearTagData || mdRfidActions.includes('delete');

        console.log('🔐 Smart API Permission Check (role.permissions):', {
          hasTagItems,
          hasManageTag,
          hasUpdateQuantity,
          hasClearTagData,
          foundPermissions: permissions
            .filter((p: any) =>
              p.menuCode === 'tag_items' ||
              p.menuCode === 'manage_tag' ||
              p.menuCode === 'md_rfid'
            )
            .map((p: any) => ({ menuCode: p.menuCode, menuName: p.menuName, action: p.action }))
        });
      }

      // (2026-07-24, Jason Cox) Industry 3 customers don't have the PDA Module
      // menu (tag_items/manage_tag) seeded for their industry, so the PDA
      // permission check above always fails for them. Admit industry-3 users
      // regardless: the PWA hides Tag Items / Manage Tags for them (they lack
      // those perms) but exposes every other feature -- see derivePermissions()
      // in AuthContext. Reason: unblock these customers without a DB change.
      const industryId = (userData as any).industryId;
      const isIndustry3 = industryId === 3 || industryId === '3';

      // User needs at least one PDA permission -- OR to be an industry-3 user --
      // to access the PWA.
      const hasPermission = hasTagItems || hasManageTag || hasUpdateQuantity || hasClearTagData || isIndustry3;

      if (!hasPermission) {
        console.warn('No required permissions found in Smart API response');
      }

      return hasPermission;
    } catch (error) {
      console.error('Error checking PDA Module permissions:', error);
      return false;
    }
  }
}
